import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Spinner,
  CisContainer,
  Button,
  Row,
  Col,
  ButtonIcon,
  Modal,
  Table,
} from 'components';
import PropTypes from 'prop-types';
import { globalPropTypes } from 'helpers';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import { downloadTrainingsImportTemplate } from '../../../api/services/training';

const COLUMN_COUNT = 7;

const Trainings = ({
  loading,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  setProfile,
  removeTraining,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [portalReady, setPortalReady] = useState(false);

  useEffect(() => {
    if (document.getElementById('cpd-mgmt-buttons-portal')) {
      setPortalReady(true);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    setSort(direction ? `${key}.${direction}` : null);
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  const handleDownload = async () => {
    try {
      setIsDownloadingTemplate(true);
      await downloadTrainingsImportTemplate();
    } catch (error) {
      console.error('Error downloading template:', error);
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.fullName || ''}
                  onFilter={(val) => handleColumnFilterChange('fullName', val)}
                  filterPlaceholder={t('page.ma.cpdManagement.trainings.fullNamePlaceholder')}
                  label={t('page.ma.cpdManagement.trainings.fullName')}
                  sort
                  sortDirection={getSortDirection('fullName')}
                  onSort={(dir) => handleColumnSort('fullName', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.email || ''}
                  onFilter={(val) => handleColumnFilterChange('email', val)}
                  filterPlaceholder={t('page.ma.cpdManagement.trainings.emailPlaceholder')}
                  label={t('page.ma.cpdManagement.trainings.email')}
                  sort
                  sortDirection={getSortDirection('email')}
                  onSort={(dir) => handleColumnSort('email', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  label={t('page.ma.cpdManagement.trainings.cpdHours')}
                  sort
                  sortDirection={getSortDirection('cpdHours')}
                  onSort={(dir) => handleColumnSort('cpdHours', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.name || ''}
                  onFilter={(val) => handleColumnFilterChange('name', val)}
                  filterPlaceholder={t('page.ma.cpdManagement.trainings.namePlaceholder')}
                  label={t('page.ma.cpdManagement.trainings.name')}
                  sort
                  sortDirection={getSortDirection('name')}
                  onSort={(dir) => handleColumnSort('name', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.completionDateFrom || ''}
                  filterValueTo={filters.completionDateTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('completionDateFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('completionDateTo', val)}
                  filterPlaceholder={t('page.ma.cpdManagement.trainings.completionDate')}
                  label={t('page.ma.cpdManagement.trainings.completionDate')}
                  sort
                  sortDirection={getSortDirection('completionDate')}
                  onSort={(dir) => handleColumnSort('completionDate', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.courseName || ''}
                  onFilter={(val) => handleColumnFilterChange('courseName', val)}
                  filterPlaceholder={t('page.ma.cpdManagement.trainings.coursePlaceholder')}
                  label={t('page.ma.cpdManagement.trainings.course')}
                  sort
                  sortDirection={getSortDirection('courseName')}
                  onSort={(dir) => handleColumnSort('courseName', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyActionHeading style={{ minWidth: '8rem' }}>
                <TableHeaderCell
                  label={t('page.ma.cpdManagement.trainings.actions')}
                  labelAlign="center"
                  isAction={true}
                />
              </CisTable.StickyActionHeading>
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={COLUMN_COUNT}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={COLUMN_COUNT}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('page.ma.cpdManagement.trainings.fullName')}</Table.Label>
                      {item.fullName}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.ma.cpdManagement.trainings.email')}</Table.Label>
                      {item.email}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.ma.cpdManagement.trainings.cpdHours')}</Table.Label>
                      {item.cpdHours}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.ma.cpdManagement.trainings.name')}</Table.Label>
                      {item.name}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>
                        {t('page.ma.cpdManagement.trainings.completionDate')}:
                      </Table.Label>
                      {item.completionDate && moment(item.completionDate).format('DD/MM/YYYY')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.ma.cpdManagement.trainings.course')}</Table.Label>
                      {item.courseName}
                    </CisTable.Cell>
                    <CisTable.StickyActionCell style={{ minWidth: '7.6rem' }}>
                      <Table.Label>{t('page.ma.cpdManagement.trainings.actions')}:</Table.Label>
                      <Row style={{ gap: '3px', justifyContent: 'center' }}>
                        <ButtonIcon
                          icon="edit"
                          secondary
                          as={Button}
                          onClick={() => {
                            setProfile(item);
                            history.push('/ma/cpd-management/trainings/edit');
                          }}
                        />
                        <ButtonIcon
                          icon="trash"
                          onClick={() => {
                            setOpenConfirmModal(true);
                            setSelectedItem(item);
                          }}
                        />
                      </Row>
                    </CisTable.StickyActionCell>
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
      <Modal
        heading={t('page.ma.cpdManagement.trainings.confirmDelete')}
        isOpen={openConfirmModal && selectedItem?.id}
        confirm={() => {
          removeTraining(selectedItem.id);
          setOpenConfirmModal(false);
        }}
        cancel={() => setOpenConfirmModal(false)}
        onRequestClose={() => setOpenConfirmModal(false)}
      />
      {portalReady &&
        ReactDOM.createPortal(
          <Row style={{ alignItems: 'center', justifyContent: 'flex-end' }}>
            <Col size={{ xs: 1 / 3 }}>
              <Button secondary as={Link} to="/ma/cpd-management/trainings/create">
                {t('page.ma.cpdManagement.trainings.new')}
              </Button>
            </Col>
            <Col size={{ xs: 1 / 3 }}>
              <Button secondary disabled={isDownloadingTemplate} onClick={handleDownload}>
                {isDownloadingTemplate ? t('global.downloading') : t('global.downloadTemplate')}
              </Button>
            </Col>
            <Col size={{ xs: 1 / 3 }}>
              <Button secondary as={Link} to="/ma/cpd-management/trainings/import">
                {t('global.import')}
              </Button>
            </Col>
          </Row>,
          document.getElementById(`cpd-mgmt-buttons-portal`),
        )}
    </PageContent>
  );
};

Trainings.propTypes = {
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  setProfile: PropTypes.func.isRequired,
  removeTraining: PropTypes.func.isRequired,
};

Trainings.defaultProps = {
  loading: false,
};

export default Trainings;
